import type { MaasInferenceServiceFormItem, MaasWorkload } from '@/delivery-type-plugin/capabilities/maas-init/maasTypes';
import { safeParseJSON } from '@/utils/json-parse';

import { deserializeServiceListFromApi } from './serviceApiField';
import type { ServiceFormItem } from './types';
import { normalizeServiceItem } from './usageScenario';

/** 从 form 字段值解析服务列表（兼容数组直存与 JSON 字符串） */
export function readServiceListFromFormValue(value: unknown): ServiceFormItem[] {
  if (Array.isArray(value)) {
    return value.map((item) => (item && typeof item === 'object' ? normalizeServiceItem(item as ServiceFormItem) : item)).filter(Boolean);
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return [];
    const parsed = safeParseJSON(trimmed, undefined);
    if (Array.isArray(parsed)) {
      return parsed
        .map((item) => (item && typeof item === 'object' ? normalizeServiceItem(item as ServiceFormItem) : item))
        .filter(Boolean);
    }
  }
  return [];
}

export function normalizeServiceList(raw: unknown): ServiceFormItem[] {
  return readServiceListFromFormValue(raw);
}

/** 从顶层 network.service 解析 name -> usageScenario */
export function buildUsageScenarioMapFromWorkload(detail?: MaasWorkload): Map<string, string> {
  const result = new Map<string, string>();
  if (!detail) return result;

  const list = deserializeServiceListFromApi(detail.spec?.network?.service as unknown[] | undefined) as
    | MaasInferenceServiceFormItem[]
    | undefined;
  if (!Array.isArray(list)) return result;

  list.forEach((item, index) => {
    const scenario = String(item?.usageScenario ?? (item as { usage?: string })?.usage ?? '').trim();
    if (!scenario) return;
    const name = String(item.name || '').trim();
    if (name) {
      result.set(name, scenario);
      return;
    }
    result.set(`__index_${index}`, scenario);
  });
  return result;
}

/** 为 status.network.service 合并 usageScenario（优先项上 usage，其次 Spec network.service） */
export function enrichNetworkServicesWithUsageScenario<T extends { name?: string; usage?: string }>(
  detail: MaasWorkload | undefined,
  services: T[],
): (T & { usageScenario?: string })[] {
  const scenarioMap = buildUsageScenarioMapFromWorkload(detail);
  return services.map((item, index) => {
    const name = String(item.name || '').trim();
    const fromUsage = String(item.usage || '').trim();
    const usageScenario = fromUsage || (name && scenarioMap.get(name)) || scenarioMap.get(`__index_${index}`);
    return usageScenario ? { ...item, usageScenario } : { ...item };
  });
}

export {
  getUniqueUsageScenarioError,
  getDuplicateServiceNameError,
  getDuplicateServicePortError,
  normalizeServiceItem,
  applyUsageScenarioToServiceItem,
  inferUsageScenarioFromLegacyItem,
  UNIQUE_USAGE_SCENARIOS,
} from './usageScenario';
export {
  deserializeServiceItemFromApi,
  deserializeServiceListFromApi,
  serializeServiceItemForApi,
  serializeServiceListForApi,
  type ServiceApiItem,
} from './serviceApiField';
